import _ from 'lodash';
import { nowUnix, unixToDate, dateToUnix } from 'utils/date';
import { produce } from 'immer';
import Initialiser from 'initialiser';
import { logout } from 'ducks/modules/user';
import store from 'ducks/configureStore';
import constants from 'utils/constants';
import api from 'utils/api';
import { ToTitleCase } from 'utils/strings';

// ACTION TYPES
const START_FETCH_PERFORMANCE_CHARTS = 'START_FETCH_PERFORMANCE_CHARTS';
const UPDATED_PERFORMANCE_CHARTS = 'UPDATED_PERFORMANCE_CHARTS';
const START_FETCH_PERFORMANCE_TOP_SONGS = 'START_FETCH_PERFORMANCE_TOP_SONGS';
const UPDATED_PERFORMANCE_TOP_SONGS = 'UPDATED_PERFORMANCE_TOP_SONGS';
const START_FETCH_PERFORMANCE_MARKET_SHARE = 'START_FETCH_PERFORMANCE_MARKET_SHARE';
const UPDATED_PERFORMANCE_MARKET_SHARE = 'UPDATED_PERFORMANCE_MARKET_SHARE';
const START_FETCH_PERFORMANCE_RANKINGS = 'START_FETCH_PERFORMANCE_RANKINGS';
const UPDATED_PERFORMANCE_RANKINGS = 'UPDATED_PERFORMANCE_RANKINGS';

// SYNC ACTION CREATORS
export function startFetchPerformanceCharts() {
    return { type: START_FETCH_PERFORMANCE_CHARTS };
}
export function updatedPerformanceCharts(charts) {
    return { type: UPDATED_PERFORMANCE_CHARTS, charts };
}
export function startFetchPerformanceTopSongs() {
    return { type: START_FETCH_PERFORMANCE_TOP_SONGS };
}
export function updatedPerformanceTopSongs(topSongs, lastUpdated) {
    return { type: UPDATED_PERFORMANCE_TOP_SONGS, topSongs, lastUpdated };
}
export function startFetchPerformanceMarketShare() {
    return { type: START_FETCH_PERFORMANCE_MARKET_SHARE };
}
export function updatedPerformanceMarketShare(marketShare, lastUpdated) {
    return { type: UPDATED_PERFORMANCE_MARKET_SHARE, marketShare, lastUpdated };
}
export function startFetchPerformanceRankings() {
    return { type: START_FETCH_PERFORMANCE_RANKINGS };
}
export function updatedPerformanceRankings(rankings, rankingsDate) {
    return { type: UPDATED_PERFORMANCE_RANKINGS, rankings, rankingsDate };
}

// ASYNC ACTION CREATORS
export function fetchPerformanceCharts() {
    return async (dispatch) => {
        const user = store.getState().user;
        dispatch(startFetchPerformanceCharts());
        try {
            const response = await api.fetchPerformanceCharts();
            // for (const key in response) {
            //     if (isNaN(response[key])) {
            //         throw new Error('Catalogue summary must be a number.');
            //     }
            //     response[key] = Number(response[key]);
            // }

            const formattedCharts = groupChartsByCountries(parseChartCity(response), user);

            dispatch(updatedPerformanceCharts(formattedCharts));
        } catch (err) {
            console.error('Error fetching performance charts: ', err);
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                dispatch(updatedPerformanceCharts(403));
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            } else if (err && err.status === constants.HttpCodes.INTERNAL_SERVER_ERROR) {
                dispatch(updatedPerformanceCharts(500));
            } else {
                dispatch(updatedPerformanceCharts([]));
            }
        }
    };
}

export function fetchPerformanceTopSongs(slug, range, country = 0) {
    return async (dispatch) => {
        dispatch(startFetchPerformanceTopSongs());
        try {
            const { timestamp } = await api.fetchRankingsLatestDate(slug);

            const endDate = (timestamp && timestamp.hasOwnProperty('seconds') && timestamp.seconds) || nowUnix();
            const startDate = endDate - range * 86400;

            const topSongs = await api.fetchPerformanceTopSongs(slug, startDate, endDate);

            const sortedTopSongs = _.orderBy(topSongs, [(o) => Number(o.date.seconds)], ['asc']);

            let isWeekly = false;
            if (country) {
                const charts = store.getState().performance.charts[`${country}`];
                const chart = charts.find((chart) => chart.slug === slug);
                isWeekly = chart && (chart.interval === 'WEEKLY' || chart.platform.includes('youtube'));
            }

            const nextTopSongs = _.cloneDeep(sortedTopSongs);

            sortedTopSongs.forEach((point, i) => {
                if (isNaN(point.count)) {
                    throw new Error('Top Songs Count must be a number');
                }
                // handle weekly charts
                if (isWeekly && point.count === 0) {
                    // Find smaller numbers and within range of past 7 days (there might be multiple points ¯\_(ツ)_/¯)
                    const pointsWithValue = sortedTopSongs.filter(
                        (p) =>
                            p.count > 0 &&
                            Number(p.date.seconds) < Number(point.date.seconds) &&
                            Number(point.date.seconds) - Number(p.date.seconds) < 7 * 24 * 60 * 60,
                    );
                    // Find all the diffs from the current point
                    const dateDiff = pointsWithValue.map((p) => Number(point.date.seconds) - Number(p.date.seconds));
                    // Find the index for the smallest interval from the current point - closest to current point
                    const idxSmallestDiff = dateDiff.indexOf(Math.min(...dateDiff));

                    // Attach that count to the final array
                    if (idxSmallestDiff > -1) {
                        nextTopSongs[i].count = pointsWithValue[idxSmallestDiff].count;
                    }
                }

                nextTopSongs[i].parsedDate = unixToDate(point.date.seconds).toISOString();
            });

            dispatch(updatedPerformanceTopSongs(nextTopSongs, endDate));
        } catch (err) {
            console.error('Error fetching top songs: ', err);
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                dispatch(updatedPerformanceTopSongs(403));
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            } else if (err && err.status === constants.HttpCodes.INTERNAL_SERVER_ERROR) {
                dispatch(updatedPerformanceTopSongs(500));
            } else {
                dispatch(updatedPerformanceTopSongs([]));
            }
        }
    };
}

export function fetchPerformanceMarketShare(slug, range, country = 0) {
    return async (dispatch) => {
        dispatch(startFetchPerformanceMarketShare());
        try {
            const { timestamp } = await api.fetchRankingsLatestDate(slug);

            const endDate = (timestamp && timestamp.hasOwnProperty('seconds') && timestamp.seconds) || nowUnix();
            const startDate = endDate - range * 86400;

            const marketShare = await api.fetchPerformanceMarketShare(slug, startDate, endDate);

            const sortedMarketShare = _.orderBy(marketShare, [(o) => Number(o.date.seconds)], ['asc']);

            let isWeekly = false;
            if (country) {
                const charts = store.getState().performance.charts[`${country}`];
                const chart = charts.find((chart) => chart.slug === slug);
                isWeekly = chart && (chart.interval === 'WEEKLY' || chart.platform.includes('youtube'));
            }

            const nextMarketShare = _.cloneDeep(sortedMarketShare);

            sortedMarketShare.forEach((point, i) => {
                if (isNaN(point.share)) {
                    throw new Error('Market Share Value must be a number');
                }
                // handle weekly charts
                if (isWeekly && point.share === 0) {
                    // Find smaller numbers and within range of past 7 days (there might be multiple points ¯\_(ツ)_/¯)
                    const pointsWithValue = sortedMarketShare.filter(
                        (p) =>
                            p.share > 0 &&
                            Number(p.date.seconds) < Number(point.date.seconds) &&
                            Number(point.date.seconds) - Number(p.date.seconds) < 7 * 24 * 60 * 60,
                    );
                    // Find all the diffs from the current point
                    const dateDiff = pointsWithValue.map((p) => Number(point.date.seconds) - Number(p.date.seconds));
                    // Find the index for the smallest interval from the current point - closest to current point
                    const idxSmallestDiff = dateDiff.indexOf(Math.min(...dateDiff));

                    // Attach that count to the final array
                    if (idxSmallestDiff > -1) {
                        nextMarketShare[i].share = pointsWithValue[idxSmallestDiff].share;
                    }
                }

                nextMarketShare[i].parsedDate = unixToDate(point.date.seconds).toISOString();
            });

            dispatch(updatedPerformanceMarketShare(nextMarketShare, endDate));
        } catch (err) {
            console.error('Error fetching market share: ', err);
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                dispatch(updatedPerformanceMarketShare(403));
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            } else if (err && err.status === constants.HttpCodes.INTERNAL_SERVER_ERROR) {
                dispatch(updatedPerformanceMarketShare(500));
            } else {
                dispatch(updatedPerformanceMarketShare([]));
            }
        }
    };
}

export function fetchPerformanceRankings(slug, latest = false, date = new Date()) {
    return async (dispatch) => {
        dispatch(startFetchPerformanceRankings());
        try {
            let endDate;
            if (latest) {
                const { timestamp } = await api.fetchRankingsLatestDate(slug);
                endDate = (timestamp && timestamp.hasOwnProperty('seconds') && timestamp.seconds) || nowUnix();
            } else if (!latest && Number(date) > 0) {
                endDate = Math.floor(Number(date));
            } else {
                endDate = dateToUnix(date);
            }

            const rankings = await api.fetchPerformanceRankings(slug, endDate);

            dispatch(updatedPerformanceRankings(rankings, endDate));
        } catch (err) {
            console.error('Error fetching rankings: ', err);
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                dispatch(updatedPerformanceRankings(403));
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            } else if (err && err.status === constants.HttpCodes.INTERNAL_SERVER_ERROR) {
                dispatch(updatedPerformanceRankings(500));
            } else {
                dispatch(updatedPerformanceRankings([]));
            }
        }
    };
}

// UTILS

function parseChartCity(charts) {
    const parsedCharts = charts.map((chart) => {
        if (
            chart.platform === 'shazam' &&
            chart.name.toLowerCase() === 'top city' &&
            chart.slug.includes('shazam-top-200-')
        ) {
            const cityName = chart.slug
                .replace(`shazam-top-200-${chart.country.toLowerCase()}-`, '')
                .split('-')
                .join(' ');
            chart.name = `${chart.name}: ${ToTitleCase(cityName)}`;
        }
        return chart;
    });
    return parsedCharts;
}

function groupChartsByCountries(charts, user) {
    const groupedCharts = {};
    const extraCountries = [];

    // Expand the countries if user has extra countries in their charts
    if (user.user_preferences && user.user_preferences.extra_countries) {
        user.user_preferences.extra_countries.forEach((country) => {
            groupedCharts[String(country)] = [];
            const alphaCode = constants.AllCountryISOAlpha2Codes[country];
            extraCountries.push(alphaCode);
        });
    }

    const supportedCountries = Object.values(constants.CountryISOAlpha2Codes).concat(extraCountries);
    charts.map((chart) => {
        const countryCode = _.findKey(constants.AllCountryISOAlpha2Codes, (c) => c === chart.country);
        if (supportedCountries.includes(chart.country)) {
            if (!groupedCharts.hasOwnProperty(countryCode)) {
                groupedCharts[countryCode] = [];
            }
            groupedCharts[countryCode].push(chart);
        }
        return null;
    });
    return groupedCharts;
}

function sortRankings(rankings) {
    let sortedRankings = [];
    if (Array.isArray(rankings) && rankings.length > 0) {
        sortedRankings = _.orderBy(rankings, [(o) => o.position], ['asc']);
    }
    return sortedRankings;
}

// REDUCER
export default function reducer(state = Initialiser.initialState.performance, action) {
    return produce(state, (draft) => {
        switch (action.type) {
            case UPDATED_PERFORMANCE_CHARTS: {
                if (action.charts > 0) {
                    draft.charts = action.charts;
                } else {
                    draft.charts = action.charts;
                }
                break;
            }
            case UPDATED_PERFORMANCE_TOP_SONGS: {
                draft.topSongs = action.topSongs;
                if (draft.lastUpdated !== action.lastUpdated) {
                    draft.lastUpdated = action.lastUpdated;
                }
                break;
            }
            case UPDATED_PERFORMANCE_MARKET_SHARE: {
                draft.marketShare = action.marketShare;
                if (draft.lastUpdated !== action.lastUpdated) {
                    draft.lastUpdated = action.lastUpdated;
                }
                break;
            }
            case UPDATED_PERFORMANCE_RANKINGS: {
                if (action.rankings > 0) {
                    draft.rankings = action.rankings;
                } else {
                    draft.rankings = sortRankings(action.rankings);
                }

                if (draft.rankingsDate !== action.rankingsDate) {
                    draft.rankingsDate = action.rankingsDate;
                }
                break;
            }

            default:
                return draft;
        }
    });
}
